import * as React from "react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

interface TriggerProps {
  icon: React.ReactNode
  /** aria-label and default tooltip text */
  label: string
  /** Override tooltip content (e.g. include a kbd chip). Defaults to label. */
  tooltip?: React.ReactNode
  onClick?: () => void
  active?: boolean
  badge?: number
  disabled?: boolean
  id?: string
  className?: string
  /** "md" = 36×36 for chart controls; "sm" = 32×32 for utility/quiet buttons */
  size?: "sm" | "md"
}

export function Trigger({
  icon,
  label,
  tooltip,
  onClick,
  active = false,
  badge,
  disabled = false,
  id,
  className,
  size = "md",
}: TriggerProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          id={id}
          type="button"
          aria-label={label}
          aria-pressed={active}
          disabled={disabled}
          onClick={onClick}
          className={cn(
            "relative inline-flex items-center justify-center",
            "text-[color:var(--lyt-text-muted)] transition-colors duration-[var(--duration-fast)]",
            "hover:bg-[var(--surface-tint-hover)] hover:text-[color:var(--lyt-text-secondary)]",
            "disabled:opacity-40 disabled:pointer-events-none",
            size === "md"
              ? "w-9 h-9 rounded-[var(--radius-md)]"
              : "w-8 h-8 rounded-[6px]",
            active && "text-[color:var(--lyt-accent)] bg-[var(--lyt-accent-dim)]",
            className
          )}
        >
          {icon}
          {badge !== undefined && badge > 0 && (
            <span
              aria-hidden
              className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] rounded-full px-[3px] flex items-center justify-center text-xxs font-medium leading-none"
              style={{
                background: "var(--lyt-accent)",
                color: "var(--lyt-accent-on)",
              }}
            >
              {badge > 9 ? "9+" : badge}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" sideOffset={6}>
        {tooltip ?? label}
      </TooltipContent>
    </Tooltip>
  )
}
